import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useLocation, useNavigate } from 'react-router-dom';
import './student-dashboard-responsive.css';
import Logo from '../../assets/images/logos/logo2.png';



const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STUDENT_ASSIGNED_QUIZ_DETAIL_API =
    process.env.REACT_APP_STUDENT_ASSIGNED_QUIZ_DETAIL_API ||
    `${API_BASE_URL}/api/admin-app/student-assigned-quiz/`;
const STUDENT_RESULT_SUBMIT_API =
    process.env.REACT_APP_STUDENT_RESULT_SUBMIT_API ||
    `${API_BASE_URL}/api/admin-app/student-results/submit/`;

const getCollegeCode = () =>
    (
        localStorage.getItem('guest_college_code') ||
        sessionStorage.getItem('guest_college_code') ||
        ''
    ).trim();

const optionMeta = [
    { key: 'A', field: 'option_a' },
    { key: 'B', field: 'option_b' },
    { key: 'C', field: 'option_c' },
    { key: 'D', field: 'option_d' },
];

const StudentTestAttemptMain = () => {
    const rootRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const quizId = (params.get('quiz_id') || '').trim();

    const [quiz, setQuiz] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [markedQuestions, setMarkedQuestions] = useState(new Set());
    const [visitedQuestions, setVisitedQuestions] = useState(new Set());
    const [timeLeft, setTimeLeft] = useState(null);
    const [examEnded, setExamEnded] = useState(false);
    const [selectedSection, setSelectedSection] = useState('');
    const [completedSections, setCompletedSections] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitMessage, setSubmitMessage] = useState('');
    const [notAllowedMessage, setNotAllowedMessage] = useState('');
    const [endExamReason, setEndExamReason] = useState('');
    const [mediaStream, setMediaStream] = useState(null);
    const [mediaError, setMediaError] = useState('');
    const [mediaReady, setMediaReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const mediaPermissionGranted = sessionStorage.getItem('exam_media_ready') === 'true';
    const hasSeenInstructions = quizId
        ? sessionStorage.getItem(`exam_instructions_seen_${quizId}`) === 'true'
        : false;

    // WebRTC References
    const pcRef = useRef(null);
    const socketRef = useRef(null);
    const studentId = useMemo(() => (localStorage.getItem('guest_student_id') || '').trim(), []);
    const studentName = useMemo(() => (localStorage.getItem('guest_student_name') || '').trim(), []);

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
        prevent_tab_switch: false,
        detect_window_blur: false,
        prevent_exit: false,
        max_violations: 5
    });
    const [violationCount, setViolationCount] = useState(0);
    const [securityMessage, setSecurityMessage] = useState('');
    const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
    const defaultSectionTimeMinutes = 1.5;
    const sectionDefinitions = useMemo(() => {
        const quizSections = Array.isArray(quiz?.sections) ? quiz.sections : [];
        const normalizedQuizSections = [];

        quizSections.forEach((section, index) => {
            const topic = (section?.topic || '').trim();
            if (!topic || normalizedQuizSections.some((item) => item.topic === topic)) return;
            const timeMinutes = Number(section?.time_minutes);
            normalizedQuizSections.push({
                topic,
                time_minutes: Number.isFinite(timeMinutes) && timeMinutes > 0 ? timeMinutes : defaultSectionTimeMinutes,
            });
        });

        if (normalizedQuizSections.length > 0) {
            return normalizedQuizSections;
        }

        const topicList = questions
            .map((question) => (question?.topic || '').trim())
            .filter((topic) => topic.length > 0);
        const uniqueTopics = Array.from(new Set(topicList));
        return (uniqueTopics.length > 0 ? uniqueTopics : ['Section 1']).map((topic) => ({
            topic,
            time_minutes: defaultSectionTimeMinutes,
        }));
    }, [quiz?.sections, questions]);
    const sections = useMemo(() => sectionDefinitions.map((section) => section.topic), [sectionDefinitions]);
    
    // Set initial selected section
    useEffect(() => {
        if (sections.length > 0 && !selectedSection) {
            setSelectedSection(sections[0]);
            setActiveIndex(0);
        }
    }, [sections, selectedSection]);

    const normalizeTopicValue = (value) => (value || '').toString().trim().toLowerCase();

    const getQuestionTopicCandidates = (question) => {
        const rawValues = [question?.topic, question?.topic_name];
        return Array.from(
            new Set(rawValues.map((value) => normalizeTopicValue(value)).filter(Boolean))
        );
    };

    const showQuizAlert = (options) => Swal.fire({
        target: document.fullscreenElement || rootRef.current || document.body,
        heightAuto: false,
        backdrop: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
            popup: 'swal2-fullscreen-safe-popup',
            container: 'swal2-fullscreen-safe-container',
        },
        didOpen: (popup) => {
            popup.style.zIndex = '999999';
        },
        confirmButtonColor: '#2f7f4d',
        ...options,
    });
    
    const filteredQuestions = useMemo(() => {
        if (!selectedSection) return [];
        const normalizedSection = normalizeTopicValue(selectedSection);
        return questions.filter((question) => getQuestionTopicCandidates(question).includes(normalizedSection));
    }, [questions, selectedSection]);
    
    const activeQuestion = filteredQuestions[activeIndex] || null;
    const selected = activeQuestion ? answers[activeQuestion.id] : null;
    const activeSection = selectedSection || sections[0];
    const activeSectionNumber = Math.max(sections.indexOf(activeSection), 0) + 1;
    const isLastQuestion = filteredQuestions.length > 0 && activeIndex === filteredQuestions.length - 1;
    const currentSectionIndex = sections.indexOf(selectedSection);
    const isLastSection = currentSectionIndex === sections.length - 1;
    const isCurrentSectionComplete = filteredQuestions.length > 0 && filteredQuestions.every((q) => Boolean(answers[q.id]));
    const answeredCount = filteredQuestions.filter((question) => Boolean(answers[question.id])).length;
    const markedCount = filteredQuestions.filter(
        (question) => markedQuestions.has(question.id) && !answers[question.id]
    ).length;
    const visitedCount = filteredQuestions.filter((question) => visitedQuestions.has(question.id)).length;
    const notAnsweredCount = Math.max(visitedCount - answeredCount - markedCount, 0);
    const notVisitedCount = Math.max(filteredQuestions.length - visitedCount, 0);

    const enterFullscreen = async () => {
        try {
            const el = rootRef.current || document.documentElement;
            if (document.fullscreenElement) return true;
            if (!el?.requestFullscreen) {
                setNotAllowedMessage('Fullscreen is not supported in this browser/device. Please use a compatible browser.');
                return false;
            }
            await el.requestFullscreen();
            return true;
        } catch (error) {
            setNotAllowedMessage('Fullscreen permission was denied. Please allow fullscreen to continue the quiz.');
            return false;
        }
    };

    const stopMediaTracks = () => {
        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
        }
    };

    const endExam = (reason) => {
        if (examEnded) return;
        setExamEnded(true);
        setEndExamReason(reason || 'Session interrupted');
    };

    const redirectToLogin = () => {
        stopMediaTracks();
        localStorage.removeItem('guest_user_role');
        localStorage.removeItem('guest_access_token');
        localStorage.removeItem('guest_refresh_token');
        localStorage.removeItem('guest_college_code');
        localStorage.removeItem('guest_college_name');
        localStorage.removeItem('guest_student_id');
        sessionStorage.removeItem('guest_user_role');
        sessionStorage.removeItem('guest_college_code');
        sessionStorage.removeItem('guest_college_name');
        sessionStorage.removeItem('guest_student_id');
        navigate('/login');
    };

    const triggerNotAllowed = (message) => {
        showQuizAlert({
            title: 'Not allowed',
            text: message || 'This action is not allowed during the quiz.',
            icon: 'warning',
            confirmButtonText: 'OK',
        });
    };

    useEffect(() => {
        if (!quizId) return;
        if (hasSeenInstructions) return;
        navigate(`/dashboard/student/test-instructions?role=student&quiz_id=${encodeURIComponent(quizId)}`, { replace: true });
    }, [hasSeenInstructions, navigate, quizId]);

    useEffect(() => {
        const fetchQuiz = async () => {
            const collegeCode = getCollegeCode();
            if (!collegeCode) {
                setErrorMessage('College session not found. Please log in again.');
                setIsLoading(false);
                return;
            }

            if (!hasSeenInstructions) {
                return;
            }

            if (!quizId) {
                setErrorMessage('Quiz ID not found. Go back to dashboard and start again.');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setErrorMessage('');
                setSubmitMessage('');
                const response = await fetch(
                    `${STUDENT_ASSIGNED_QUIZ_DETAIL_API}${encodeURIComponent(quizId)}/?college_code=${encodeURIComponent(collegeCode)}`
                );
                const data = await response.json();
                if (!response.ok) {
                    setErrorMessage(data?.error || 'Unable to load quiz right now.');
                    setQuiz(null);
                    return;
                }
                setQuiz(data);
                setActiveIndex(0);
                setSelectedSection('');
                setCompletedSections(new Set());
                setMarkedQuestions(new Set());
                setVisitedQuestions(new Set());
                setAnswers({});
            } catch (error) {
                setErrorMessage('Unable to load quiz right now.');
                setQuiz(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuiz();
    }, [hasSeenInstructions, quizId]);

    useEffect(() => {
        const fetchSecuritySettings = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/admin-app/security-settings/`);
                if (response.ok) {
                    const data = await response.json();
                    setSecuritySettings(data);
                }
            } catch (error) {
                console.error('Failed to fetch security settings:', error);
            }
        };
        fetchSecuritySettings();
    }, []);

    const logViolation = async (type) => {
        const studentIdLocal = localStorage.getItem('guest_student_id');
        const studentNameLocal = localStorage.getItem('guest_student_name');
        if (!quizId) return;

        try {
            await fetch(`${API_BASE_URL}/api/admin-app/violation-log/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    student: studentIdLocal ? parseInt(studentIdLocal) : null,
                    student_name: studentNameLocal || 'Guest Student',
                    exam: parseInt(quizId),
                    type: type
                })
            });
        } catch (error) {
            console.error('Failed to log violation:', error);
        }

        setViolationCount(prev => {
            const next = prev + 1;
            if (securitySettings.max_violations > 0 && next >= securitySettings.max_violations) {
                handleSubmit('Exceeded maximum allowed security violations');
            }
            return next;
        });

        setSecurityMessage(`Suspicious activity detected (${type}). This violation has been logged.`);
        setTimeout(() => setSecurityMessage(''), 5000);
    };

    useEffect(() => {
        if (examEnded) return;
        if (!mediaPermissionGranted) {
            setMediaError('Camera and microphone access are required. Please allow access on the instructions page.');
            setMediaReady(false);
            return;
        }
        let isMounted = true;

        const requestMedia = async () => {
            try {
                setMediaError('');
                const stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { width: 320, height: 240, frameRate: 15 }, 
                    audio: false 
                });
                if (!isMounted) return;
                setMediaStream(stream);
                setMediaReady(true);
            } catch (error) {
                if (!isMounted) return;
                setMediaError('Camera access is required to start the exam.');
                setMediaReady(false);
            }
        };

        requestMedia();

        return () => {
            isMounted = false;
        };
    }, [examEnded, mediaPermissionGranted]);

    useEffect(() => {
        if (!mediaReady || !mediaStream || examEnded || !quizId) return;

        const connectWebRTC = async () => {
            try {
                const wsUrl = `${WS_BASE_URL}/ws/webrtc/${quizId}/`;
                const socket = new WebSocket(wsUrl);
                socketRef.current = socket;

                const configuration = {
                    iceServers: [
                        { urls: 'stun:stun.l.google.com:19302' },
                        { 
                            urls: 'turn:free.expressturn.com:3478',
                            username: '000000002093321194',
                            credential: 'DUfLd05B+q+kVlyVv4r/cMfJZhI='
                        }
                    ]
                };

                const pc = new RTCPeerConnection(configuration);
                pcRef.current = pc;

                // Add tracks to peer connection
                mediaStream.getTracks().forEach(track => {
                    pc.addTrack(track, mediaStream);
                });

                pc.onicecandidate = (event) => {
                    if (event.candidate && socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({
                            type: 'ice_candidate',
                            candidate: event.candidate,
                            student_id: studentId
                        }));
                    }
                };

                const sendOffer = async () => {
                    if (!pcRef.current) return;
                    const offer = await pcRef.current.createOffer({
                        offerToReceiveAudio: false,
                        offerToReceiveVideo: false
                    });
                    await pcRef.current.setLocalDescription(offer);
                    socket.send(JSON.stringify({
                        type: 'offer',
                        offer: offer,
                        student_id: studentId,
                        student_name: studentName
                    }));
                };

                socket.onopen = async () => {
                    console.log('WebSocket connected');
                    // Notify that a student has joined
                    socket.send(JSON.stringify({
                        type: 'join',
                        role: 'student',
                        student_id: studentId,
                        student_name: studentName
                    }));

                    await sendOffer();
                };

                socket.onmessage = async (event) => {
                    const data = JSON.parse(event.data);
                    if (data.type === 'answer' && data.student_id === studentId) {
                        await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
                    } else if (data.type === 'ice_candidate' && data.student_id === studentId && data.candidate) {
                        try {
                            await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
                        } catch (e) {
                            console.error('Error adding ice candidate', e);
                        }
                    } else if (data.type === 'monitor_joined') {
                        console.log('Monitor joined, re-sending offer...');
                        await sendOffer();
                    }
                };

                socket.onclose = () => {
                    console.log('WebSocket disconnected, retrying in 3s...');
                    if (!examEnded) {
                        setTimeout(connectWebRTC, 3000);
                    }
                };

                socket.onerror = (error) => {
                    console.error('WebSocket error:', error);
                };

            } catch (err) {
                console.error('WebRTC Connection error:', err);
            }
        };

        connectWebRTC();

        return () => {
            if (socketRef.current) socketRef.current.close();
            if (pcRef.current) pcRef.current.close();
        };
    }, [mediaReady, mediaStream, examEnded, quizId, studentId, studentName]);

    useEffect(() => {
        return () => {
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [mediaStream]);

    useEffect(() => {
        if (isLoading || errorMessage || examEnded || !mediaReady) return;
        if (!quizId) return;

        if (securitySettings.require_fullscreen) {
            enterFullscreen();
        }

        const onFullscreenChange = () => {
            if (!document.fullscreenElement && securitySettings.require_fullscreen) {
                endExam('Fullscreen exited');
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible' && securitySettings.prevent_tab_switch) {
                logViolation('tab_switch');
            }
        };

        const onWindowBlur = () => {
            if (securitySettings.detect_window_blur) {
                logViolation('blur');
            }
        };

        const onBeforeUnload = (e) => {
            if (securitySettings.prevent_exit && !examEnded) {
                logViolation('exit');
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        const onKeyDown = (e) => {
            const key = (e.key || '').toLowerCase();

            // Note: OS-level Alt+Tab cannot be reliably blocked in browsers.
            if (key === 'escape') {
                if (securitySettings.require_fullscreen) {
                    e.preventDefault();
                    e.stopPropagation();
                    triggerNotAllowed('Escape is not allowed during the quiz.');
                }
                return;
            }

            if (securitySettings.block_system_keys) {
                if (key === 'alt' || e.altKey) {
                    e.preventDefault();
                    triggerNotAllowed('Alt key shortcuts are not allowed during the quiz.');
                    return;
                }

                if (e.metaKey) {
                    e.preventDefault();
                    triggerNotAllowed('System shortcuts are not allowed during the quiz.');
                    return;
                }

                // Common devtools shortcuts
                if ((e.ctrlKey || e.metaKey) && (key === 'i' || key === 'j' || key === 'u')) {
                    e.preventDefault();
                    triggerNotAllowed('This action is not allowed during the quiz.');
                }
            }
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('blur', onWindowBlur);
        window.addEventListener('keydown', onKeyDown, { capture: true });
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('blur', onWindowBlur);
            window.removeEventListener('keydown', onKeyDown, { capture: true });
        };
}, [isLoading, errorMessage, quizId, examEnded, answers, filteredQuestions.length, navigate, mediaReady, securitySettings]);

    const handleSelect = (questionId, optionKey) => {
        if (examEnded) return;
        setAnswers((prev) => ({ ...prev, [questionId]: optionKey }));
    };

    const goPrev = () => {
        if (examEnded) return;
        setActiveIndex((idx) => Math.max(0, idx - 1));
    };
    const goNext = () => {
        if (examEnded) return;
        setActiveIndex((idx) => Math.min(filteredQuestions.length - 1, idx + 1));
    };
    
    const handleSectionChange = (section) => {
        if (examEnded) return;
        const sectionIndex = sections.indexOf(section);

        if (completedSections.has(section) && section !== selectedSection) {
            showQuizAlert({
                title: 'Section Locked',
                text: `Section ${sectionIndex + 1} has already been submitted and cannot be accessed again.`,
                icon: 'info',
                confirmButtonText: 'OK',
            });
            return;
        }
        
        // Can only access sections in order - must complete previous section first
        if (sectionIndex > 0 && !completedSections.has(sections[sectionIndex - 1])) {
            showQuizAlert({
                title: 'Complete Previous Section',
                text: `Please complete ${sections[sectionIndex - 1]} first before moving to Section ${sectionIndex + 1}.`,
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }
        
        setSelectedSection(section);
        setActiveIndex(0);
    };

    const handleSubmit = async (reason, requireConfirmation = false) => {
        if (examEnded || isSubmitting) return;

        if (requireConfirmation) {
            const result = await showQuizAlert({
                title: 'Submit exam?',
                text: 'Are you sure you want to submit the exam? You will not be able to change your answers.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, submit',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                customClass: {
                    popup: 'swal2-custom-popup',
                },
                buttonsStyling: true,
                confirmButtonColor: '#2f7f4d',
            });
            if (!result.isConfirmed) return;
        }

        const studentId = (localStorage.getItem('guest_student_id') || '').trim();
        const studentName = (localStorage.getItem('guest_student_name') || '').trim();
        const studentEmail = (localStorage.getItem('guest_student_email') || '').trim();
        const collegeCode = (localStorage.getItem('guest_college_code') || sessionStorage.getItem('guest_college_code') || '').trim();

        if ((!studentId && !studentName && !studentEmail) || !collegeCode || !quizId) {
            setSubmitMessage('Unable to submit: missing student or college information.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(STUDENT_RESULT_SUBMIT_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    student_id: Number(studentId),
                    student_name: studentName,
                    student_email: studentEmail,
                    test_id: Number(quizId),
                    college_code: collegeCode,
                    answers,
                }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setSubmitMessage(data?.error || `Unable to submit results. (${response.status})`);
                return;
            }

            const data = await response.json();
            const scoreText = data?.score !== undefined && data?.total_marks !== undefined
                ? `Score ${data.score} / ${data.total_marks}`
                : 'Submitted successfully.';
            setSubmitMessage(`Submitted. ${scoreText} ${reason ? `(${reason})` : ''}`);
            setExamEnded(true);
            stopMediaTracks();
            sessionStorage.removeItem(`exam_instructions_seen_${quizId}`);
            try {
                const raw = localStorage.getItem('attended_quizzes') || '[]';
                const list = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
                if (!list.includes(String(quizId))) {
                    list.push(String(quizId));
                    localStorage.setItem('attended_quizzes', JSON.stringify(list));
                }
            } catch (err) {
                localStorage.setItem('attended_quizzes', JSON.stringify([String(quizId)]));
            }
            navigate('/dashboard/student');
        } catch (error) {
            setSubmitMessage('Unable to submit results right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMarkAndNext = () => {
        if (!activeQuestion) return;
        if (examEnded) return;
        setMarkedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(activeQuestion.id)) {
                next.delete(activeQuestion.id);
            } else {
                next.add(activeQuestion.id);
            }
            return next;
        });
        goNext();
    };

    const handleClearResponse = () => {
        if (!activeQuestion) return;
        if (examEnded) return;
        setAnswers((prev) => {
            const next = { ...prev };
            delete next[activeQuestion.id];
            return next;
        });
    };

    const handleSaveAndNext = async () => {
        if (examEnded) return;
        
        // If last question of current section (but not last section)
        if (isLastQuestion && !isLastSection) {
            const nextSectionIndex = currentSectionIndex + 1;
            const result = await showQuizAlert({
                title: `Submit Section ${currentSectionIndex + 1}?`,
                text: `This section cannot be accessed after submitting. Once confirmed, you will enter Section ${nextSectionIndex + 1}.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: `Enter Section ${nextSectionIndex + 1}`,
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                confirmButtonColor: '#2f7f4d',
            });
            if (!result.isConfirmed) return;
            
            // Mark section as complete and move to next
            setCompletedSections((prev) => {
                const next = new Set(prev);
                next.add(selectedSection);
                return next;
            });
            
            if (nextSectionIndex < sections.length) {
                setSelectedSection(sections[nextSectionIndex]);
                setActiveIndex(0);
            }
            return;
        }
        
        // If last question of last section
        if (isLastQuestion && isLastSection) {
            handleSubmit(undefined, true);
            return;
        }
        
        // Normal next
        goNext();
    };

    const formatTime = (seconds) => {
        if (typeof seconds !== 'number') return '--:--';
        const safe = Math.max(0, seconds);
        const mins = Math.floor(safe / 60);
        const secs = safe % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    useEffect(() => {
        if (!activeQuestion) return;
        setVisitedQuestions((prev) => {
            const next = new Set(prev);
            next.add(activeQuestion.id);
            return next;
        });
    }, [activeQuestion, activeQuestion?.id]);

    useEffect(() => {
        if (examEnded) return;
        if (!selectedSection) return;

        const sectionConfig = sectionDefinitions.find((section) => section.topic === selectedSection);
        const durationMinutes = Number(sectionConfig?.time_minutes);
        const durationSeconds = Number.isFinite(durationMinutes) && durationMinutes > 0
            ? Math.max(1, Math.round(durationMinutes * 60))
            : 90;

        setTimeLeft(durationSeconds);
    }, [selectedSection, sectionDefinitions, examEnded]);

    useEffect(() => {
        if (examEnded) return undefined;
        if (typeof timeLeft !== 'number') return undefined;
        if (timeLeft <= 0) return undefined;
        const timer = window.setInterval(() => {
            setTimeLeft((prev) => (typeof prev === 'number' ? prev - 1 : prev));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [timeLeft, examEnded]);

    useEffect(() => {
        if (examEnded) return;
        if (typeof timeLeft !== 'number') return;
        if (timeLeft > 0) return;
        if (!sections.length || currentSectionIndex >= sections.length - 1) {
            handleSubmit('Time up');
        } else {
            setCompletedSections((prev) => {
                const next = new Set(prev);
                if (selectedSection) {
                    next.add(selectedSection);
                }
                return next;
            });
            setSelectedSection(sections[currentSectionIndex + 1]);
            setActiveIndex(0);
        }
    }, [timeLeft, examEnded, currentSectionIndex, sections, selectedSection, handleSubmit]);

    return (
        <div ref={rootRef} className="student-quiz-fullscreen">
            <header className="student-quiz-topbar">
                <div className="student-quiz-title">
                    <span className="student-quiz-title-text">MindMetric Part A: Foundation Section - {activeSectionNumber}</span>
                </div>
                <div className="student-quiz-topbar-right">
                    <div className="student-quiz-topbar-timer">Time Left: {formatTime(timeLeft)}</div>
                    <div className="student-quiz-brand">
                        <img src={Logo} alt="Skill Vertex" />
                    </div>
                </div>
            </header>

            <section className="student-quiz-sections">
                <div className="student-quiz-sections-label">Sections</div>
                <div className="student-quiz-sections-list">
                    {sections.map((section, index) => {
                        const isLocked = index > 0 && !completedSections.has(sections[index - 1]);
                        return (
                            <button
                                type="button"
                                key={section}
                                onClick={() => handleSectionChange(section)}
                                disabled={isLocked || examEnded}
                                className={`student-quiz-section-pill${section === activeSection ? ' is-active' : ''}${isLocked ? ' is-locked' : ''}`}
                            >
                                {isLocked && <i className="fas fa-lock" style={{ marginRight: '4px' }}></i>}
                                Section{index + 1} <span className="section-badge">{index + 1}</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {isLoading ? (
                <div className="student-quiz-loading">Loading questions...</div>
            ) : errorMessage ? (
                <div className="student-quiz-error" role="alert">
                    {errorMessage}
                </div>
            ) : mediaError ? (
                <div className="student-quiz-error" role="alert">
                    {mediaError}
                </div>
            ) : !mediaReady ? (
                <div className="student-quiz-loading">Requesting camera and microphone access...</div>
            ) : filteredQuestions.length === 0 ? (
                <div className="student-quiz-warning" role="alert">
                    No questions found for this quiz.
                </div>
            ) : (
                <div className="student-quiz-body">
                    {submitMessage && <div className="student-quiz-success">{submitMessage}</div>}
                    {securityMessage && (
                        <div className="alert alert-warning position-fixed top-0 start-50 translate-middle-x mt-3 shadow-lg" style={{ zIndex: 9999 }}>
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {securityMessage}
                        </div>
                    )}
                    
                    {violationCount > 0 && (
                        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 9998 }}>
                            <span className="badge bg-danger p-2 shadow">
                                Violations: {violationCount} / {securitySettings.max_violations}
                            </span>
                        </div>
                    )}

                    <main className="student-quiz-main">
                        <div className="student-quiz-left">
                            <div className="student-quiz-panel">
                                <div className="student-quiz-panel-title">Question No {activeIndex + 1}</div>
                                <div className="student-quiz-panel-content">
                                    <h5 className="student-quiz-panel-subtitle">Directions [Set of {filteredQuestions.length} Questions]</h5>
                                    <p className="student-quiz-panel-text">
                                        {activeQuestion?.passage ||
                                            activeQuestion?.instructions ||
                                            'Read the question on the right carefully and select the best answer.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="student-quiz-center">
                            <div className="student-quiz-panel">
                                <div className="student-quiz-question-title">
                                    Question No {activeIndex + 1}
                                    {activeQuestion?.marks ? ` (${activeQuestion.marks} marks)` : ''}
                                </div>
                                <div className="student-quiz-question-text">{activeQuestion?.prompt}</div>
                                <div className="student-quiz-options">
                                    {optionMeta.map((opt) => {
                                        const label = activeQuestion?.[opt.field];
                                        if (!label) return null;
                                        const checked = selected === opt.key;
                                        return (
                                            <label key={opt.key} className="student-quiz-option">
                                                <input
                                                    type="radio"
                                                    name={`q_${activeQuestion.id}`}
                                                    value={opt.key}
                                                    checked={checked}
                                                    onChange={() => handleSelect(activeQuestion.id, opt.key)}
                                                />
                                                <span>{opt.key})</span>
                                                <span>{label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="student-quiz-actions">
                                <button type="button" className="student-quiz-btn outline" onClick={handleMarkAndNext} disabled={examEnded}>
                                    Mark for Review & Next
                                </button>
                                <button type="button" className="student-quiz-btn outline" onClick={handleClearResponse} disabled={examEnded}>
                                    Clear Response
                                </button>
                                <button type="button" className="student-quiz-btn primary" onClick={handleSaveAndNext} disabled={examEnded}>
                                    {isLastQuestion && !isLastSection ? 'Complete Section & Next' : isLastQuestion && isLastSection ? 'Submit' : 'Save & Next'}
                                </button>
                            </div>
                        </div>

                        <aside className="student-quiz-right">
                            <div className="student-quiz-meta">
                                <div className="student-quiz-avatar" />
                            </div>

                            <div className="student-quiz-legend">
                                <div className="student-quiz-legend-title">Legend</div>
                                <div className="student-quiz-legend-list">
                                    <div className="student-quiz-legend-item">
                                        <span className="legend-pill answered">{answeredCount}</span>
                                        <span>Answered</span>
                                    </div>
                                    <div className="student-quiz-legend-item">
                                        <span className="legend-pill not-answered">{notAnsweredCount}</span>
                                        <span>Not Answered</span>
                                    </div>
                                    <div className="student-quiz-legend-item">
                                        <span className="legend-pill marked">{markedCount}</span>
                                        <span>Marked</span>
                                    </div>
                                    <div className="student-quiz-legend-item">
                                        <span className="legend-pill not-visited">{notVisitedCount}</span>
                                        <span>Not Visited</span>
                                    </div>
                                </div>
                            </div>

                            <div className="student-quiz-palette">
                                <div className="student-quiz-section-note">You are viewing Section {activeSectionNumber}</div>
                                <div className="student-quiz-palette-title">Question Palette</div>
                                <div className="student-quiz-palette-grid">
                                    {filteredQuestions.map((question, index) => {
                                        const isAnswered = Boolean(answers[question.id]);
                                        const isMarked = markedQuestions.has(question.id) && !isAnswered;
                                        const isVisited = visitedQuestions.has(question.id);
                                        let statusClass = 'not-visited';
                                        if (isAnswered) statusClass = 'answered';
                                        else if (isMarked) statusClass = 'marked';
                                        else if (isVisited) statusClass = 'not-answered';
                                        return (
                                            <button
                                                type="button"
                                                key={question.id}
                                                className={`student-quiz-palette-item ${statusClass}${index === activeIndex ? ' is-active' : ''}`}
                                                onClick={() => {
                                                    if (!examEnded) setActiveIndex(index);
                                                }}
                                                disabled={examEnded}
                                            >
                                                {index + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="student-quiz-footer-actions">
                                <button
                                    type="button"
                                    className="student-quiz-btn outline"
                                    onClick={goPrev}
                                    disabled={activeIndex === 0}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="student-quiz-btn primary"
                                    onClick={() => handleSubmit(undefined, true)}
                                    disabled={examEnded || isSubmitting}
                                >
                                    Submit
                                </button>
                            </div>
                        </aside>
                    </main>
                </div>
            )}

            {examEnded && endExamReason && (
                <div className="student-quiz-modal" role="dialog" aria-modal="true">
                    <div className="student-quiz-modal-card">
                        <h5 className="mb-2">Exam ended</h5>
                        <p className="mb-3">{endExamReason}. You will be redirected to login.</p>
                        <div className="student-quiz-modal-actions">
                            <button type="button" className="student-quiz-btn primary" onClick={redirectToLogin}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentTestAttemptMain;

